import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, ListOrdered, Sparkles } from 'lucide-react';
import { ChatService } from '../../features/chat/ChatService';
import { applyViaHybridEngine } from '../../features/hybridApply/hybridEngine';
import { previewHybridOps } from '../../features/hybridApply/previewHybrid';
import {
  foldHybridCleanTarget,
  resolveSuggestedEditPreviewHtml,
} from '../../features/hybridApply/suggestedEdit';
import { RobustXmlTrackChangesGenerator } from '../../features/rephrasing/RobustXmlTrackChangesGenerator';
import { DocumentReader } from '../../word/api/DocumentReader';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { flattenFindingsChronologically } from '../../utils/reviewFindingsOrder';
import { resolveTrackChangeAuthorSide } from '../../utils/reviewPartyContext';
import { modelMarkupToPreviewHtml, formatOoxmlParagraphForModel, stripTrackChangeMarkup } from '../../utils/trackChangesModelText';
import {
  ooxmlToPartyTrackChangesHtml,
  prepareTrackChangesPreviewHtml,
} from '../../utils/trackChangePreviewHtml';
import { detectTextDirection } from '../../utils/textUtils';
import { directionForLanguage } from '../../i18n/config';
import { logError } from '../../utils/logging';
import './CrossCheckClauseCard.css';
import './DocumentReviewResults.css';

type ReviewViewMode = 'severity' | 'chronological';

interface ReviewFinding {
  paragraphIndex: number;
  explanation: {
    ourSideInterest: string;
    theirSideInterest: string;
    whyCritical?: string;
    whyWarning?: string;
    whyInfo?: string;
  };
}

interface ReviewFindings {
  critical: ReviewFinding[];
  warning: ReviewFinding[];
  info: ReviewFinding[];
}

interface DocumentReviewResultsProps {
  findings: ReviewFindings | null;
  expandedCategories: Set<string>;
  onToggleCategory: (category: string) => void;
  paragraphs: Array<{ index: number; text: string }>;
  onRestart?: () => void;
  chatService?: ChatService;
}

type AiSuggestion = {
  status: 'loading' | 'ready' | 'error' | 'applying';
  processedText?: string;
  previewHtml?: string;
  error?: string;
};

const LEADING_CLAUSE_NUMBER_RE = /^\s*(\d+(?:\.\d+)*)[.\s]/;

function extractLeadingClauseNumber(text: string): string | null {
  const cleaned = text.replace(/[\u200f\u200e\u061c]/g, '');
  const leading = cleaned.match(LEADING_CLAUSE_NUMBER_RE);
  if (leading?.[1]) return leading[1];
  const labeled = cleaned.match(/סעיף\s+(\d+(?:\.\d+)*)/);
  return labeled?.[1] ?? null;
}

function uniqueFindingIndices(findings: ReviewFindings): number[] {
  const set = new Set<number>();
  for (const category of ['critical', 'warning', 'info'] as const) {
    for (const finding of findings[category] || []) {
      if (Number.isInteger(finding.paragraphIndex) && finding.paragraphIndex >= 0) {
        set.add(finding.paragraphIndex);
      }
    }
  }
  return [...set];
}

export const DocumentReviewResults: React.FC<DocumentReviewResultsProps> = ({
  findings,
  expandedCategories,
  onToggleCategory,
  paragraphs,
  onRestart,
  chatService,
}) => {
  const { t, i18n } = useTranslation('taskpane');
  const chromeDir = directionForLanguage(i18n.language);
  const [expandedExplanations, setExpandedExplanations] = React.useState<Set<string>>(new Set());
  const [paragraphOoxml, setParagraphOoxml] = React.useState<Record<number, string>>({});
  const [clauseNumbers, setClauseNumbers] = React.useState<Record<number, string>>({});
  const [aiByKey, setAiByKey] = React.useState<Record<string, AiSuggestion>>({});
  const [hideInsDelMarkup, setHideInsDelMarkup] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<ReviewViewMode>(() => {
    try {
      return localStorage.getItem('documentReviewViewMode') === 'chronological'
        ? 'chronological'
        : 'severity';
    } catch {
      return 'severity';
    }
  });

  React.useEffect(() => {
    if (!findings) {
      setParagraphOoxml({});
      setClauseNumbers({});
      return undefined;
    }
    const indices = uniqueFindingIndices(findings);
    let cancelled = false;
    void DocumentReader.getParagraphOoxmlByIndices(indices)
      .then((map) => {
        if (!cancelled) setParagraphOoxml(map);
      })
      .catch((err) => {
        logError('[DocumentReviewResults] Failed to load paragraph OOXML', err);
        if (!cancelled) setParagraphOoxml({});
      });
    void Promise.all(
      indices.map(async (index) => {
        const number = await DocumentReader.getClauseNumberForParagraphIndex(index);
        return [index, number] as const;
      }),
    )
      .then((pairs) => {
        if (cancelled) return;
        const map: Record<number, string> = {};
        for (const [index, number] of pairs) {
          if (number) map[index] = number.replace(/\.$/, '');
        }
        setClauseNumbers(map);
      })
      .catch((err) => {
        logError('[DocumentReviewResults] Failed to load clause numbers', err);
        if (!cancelled) setClauseNumbers({});
      });
    return () => {
      cancelled = true;
    };
  }, [findings]);

  const getParagraphText = (index: number): string => {
    const paragraph = paragraphs.find((p) => p.index === index);
    return paragraph ? paragraph.text : t('review.paragraphNotFound', { index });
  };

  const chronologicalFindings = React.useMemo(
    () => (findings ? flattenFindingsChronologically(findings) : []),
    [findings],
  );

  const toggleViewMode = () => {
    const next: ReviewViewMode = viewMode === 'chronological' ? 'severity' : 'chronological';
    setViewMode(next);
    try {
      localStorage.setItem('documentReviewViewMode', next);
    } catch (err) {
      console.error('Error saving review view mode:', err);
    }
  };

  const toggleExplanation = (key: string) => {
    setExpandedExplanations((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const navigateToParagraph = async (paragraphIndex: number) => {
    try {
      await DocumentReader.navigateToParagraph(paragraphIndex);
    } catch (err) {
      console.error('Failed to navigate to paragraph:', err);
    }
  };

  const handleAiSuggest = async (
    key: string,
    finding: ReviewFinding,
    category: 'critical' | 'warning' | 'info',
  ) => {
    if (!chatService) return;
    const current = aiByKey[key];
    if (current?.status === 'loading' || current?.status === 'applying') return;

    setAiByKey((prev) => ({ ...prev, [key]: { status: 'loading' } }));
    const whyText =
      finding.explanation.whyCritical ||
      finding.explanation.whyWarning ||
      finding.explanation.whyInfo ||
      '';
    const paragraphText = getParagraphText(finding.paragraphIndex);
    let ooxml = paragraphOoxml[finding.paragraphIndex];
    if (!ooxml) {
      try {
        const fetched = await DocumentReader.getParagraphOoxmlByIndices([finding.paragraphIndex]);
        ooxml = fetched[finding.paragraphIndex];
        if (ooxml) {
          setParagraphOoxml((prev) => ({ ...prev, ...fetched }));
        }
      } catch (err) {
        logError('[DocumentReviewResults] Failed to load OOXML for AI suggest', err);
      }
    }
    const sourceText =
      (ooxml
        ? formatOoxmlParagraphForModel(ooxml, resolveTrackChangeAuthorSide)
        : '') || paragraphText;

    try {
      const query = t('review.aiPrompt', {
        severity: t(`review.categories.${category}`),
        our: finding.explanation.ourSideInterest,
        their: finding.explanation.theirSideInterest,
        why: whyText,
      });
      const result = await chatService.processQuery(query, sourceText);
      const processedText = (result.processedText || '').trim();
      if (!processedText) {
        setAiByKey((prev) => ({
          ...prev,
          [key]: { status: 'error', error: t('review.aiFailed') },
        }));
        return;
      }

      const target = foldHybridCleanTarget(processedText);
      const hybrid = await previewHybridOps(finding.paragraphIndex, target);
      let fallbackHtml: string | null = null;
      if (ooxml) {
        try {
          const edited = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
            ooxml,
            processedText,
            'AI Suggestion',
          );
          fallbackHtml = edited ? TrackChangesParser.convertOoxmlToHtml(edited) : null;
        } catch (err) {
          logError('[DocumentReviewResults] OOXML suggestion preview failed', err);
        }
      }
      const rawPreview =
        resolveSuggestedEditPreviewHtml({
          hybridEnabled: true,
          hybridPreviewHtml: hybrid?.html ?? null,
          fallbackHtml,
        }) || processedText;
      const previewHtml = prepareTrackChangesPreviewHtml(rawPreview);

      setAiByKey((prev) => ({
        ...prev,
        [key]: { status: 'ready', processedText, previewHtml },
      }));
    } catch (err) {
      logError('[DocumentReviewResults] AI suggest failed', err);
      setAiByKey((prev) => ({
        ...prev,
        [key]: { status: 'error', error: t('review.aiFailed') },
      }));
    }
  };

  const handleAiApply = async (key: string, finding: ReviewFinding) => {
    const suggestion = aiByKey[key];
    const processedText = suggestion?.processedText?.trim();
    if (!processedText || suggestion.status === 'applying') return;

    setAiByKey((prev) => ({ ...prev, [key]: { ...suggestion, status: 'applying' } }));
    try {
      const hybrid = await applyViaHybridEngine(
        finding.paragraphIndex,
        foldHybridCleanTarget(processedText),
      );
      if (!hybrid.applied) {
        setAiByKey((prev) => ({
          ...prev,
          [key]: { ...suggestion, status: 'ready', error: t('review.aiApplyFailed') },
        }));
        return;
      }
      const refreshed = await DocumentReader.getParagraphOoxmlByIndices([finding.paragraphIndex]);
      setParagraphOoxml((prev) => ({ ...prev, ...refreshed }));
      setAiByKey((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      logError('[DocumentReviewResults] AI apply failed', err);
      setAiByKey((prev) => ({
        ...prev,
        [key]: { ...suggestion, status: 'ready', error: t('review.aiApplyFailed') },
      }));
    }
  };

  const dismissAi = (key: string) => {
    setAiByKey((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const renderFinding = (
    finding: ReviewFinding,
    category: 'critical' | 'warning' | 'info',
    index: number,
    options?: { showSeverity?: boolean },
  ) => {
    const key = `${category}-${index}`;
    const isExplanationExpanded = expandedExplanations.has(key);
    const paragraphText = getParagraphText(finding.paragraphIndex);
    const plainParagraphText = stripTrackChangeMarkup(paragraphText);
    const ooxml = paragraphOoxml[finding.paragraphIndex];
    const markupHtml = modelMarkupToPreviewHtml(paragraphText, resolveTrackChangeAuthorSide);
    const markupPreview = markupHtml ? prepareTrackChangesPreviewHtml(markupHtml) : null;
    const ooxmlHtml = ooxml ? ooxmlToPartyTrackChangesHtml(ooxml) : null;
    // OOXML/MCC HTML is the Word-faithful view (nested w:del inside w:ins). Markup is first paint.
    const liveHtml = ooxmlHtml ?? markupPreview;
    const whyText =
      finding.explanation.whyCritical ||
      finding.explanation.whyWarning ||
      finding.explanation.whyInfo ||
      '';
    const ai = aiByKey[key];
    const isAiProcessing = ai?.status === 'loading';
    const isAiReady = Boolean(
      ai && (ai.status === 'ready' || ai.status === 'applying') && ai.previewHtml,
    );
    const displayHtml = isAiReady && ai.previewHtml ? ai.previewHtml : liveHtml;
    const paragraphDir = detectTextDirection(
      isAiReady ? ai?.processedText || plainParagraphText : plainParagraphText,
    );
    const clauseNumber =
      clauseNumbers[finding.paragraphIndex] || extractLeadingClauseNumber(plainParagraphText);
    const clauseLabel = clauseNumber
      ? t('review.clauseNumber', { number: clauseNumber })
      : t('review.paragraphLabel', { number: finding.paragraphIndex + 1 });

    const actionButtons = (
      <div className="finding-actions">
        <button
          type="button"
          className="review-navigate-btn"
          onClick={() => navigateToParagraph(finding.paragraphIndex)}
          title={t('review.goToParagraph')}
        >
          <Eye size={12} />
        </button>
        <button
          type="button"
          className="explanation-badge"
          onClick={() => toggleExplanation(key)}
          title={t('review.whyExplanation')}
        >
          {isExplanationExpanded ? t('review.hide') : t('review.why')}
        </button>
        {chatService ? (
          <button
            type="button"
            className="review-ai-btn"
            onClick={() => void handleAiSuggest(key, finding, category)}
            disabled={ai?.status === 'loading' || ai?.status === 'applying'}
            title={t('review.aiTitle')}
          >
            {ai?.status === 'loading' ? (
              <span className="review-ai-spinner" aria-hidden="true" />
            ) : (
              <Sparkles size={12} />
            )}
            {ai?.status === 'loading' ? t('review.aiWorking') : t('review.ai')}
          </button>
        ) : null}
        {isAiReady && ai ? (
          <>
            <button
              type="button"
              className="review-ai-replace-btn"
              onClick={() => void handleAiApply(key, finding)}
              disabled={ai.status === 'applying'}
            >
              {ai.status === 'applying' ? t('review.aiReplacing') : t('review.aiReplace')}
            </button>
            <button
              type="button"
              className="review-ai-dismiss-btn"
              onClick={() => dismissAi(key)}
              disabled={ai.status === 'applying'}
              title={t('review.aiDismiss')}
            >
              {t('review.aiDismiss')}
            </button>
          </>
        ) : null}
      </div>
    );

    return (
      <div key={key} className={`review-finding ${options?.showSeverity ? `severity-${category}` : ''}`}>
        <div className="finding-header">
          <span className="finding-clause">{clauseLabel}</span>
          {options?.showSeverity && (
            <span className={`finding-severity ${category}`}>
              {t(`review.categories.${category}`)}
            </span>
          )}
        </div>

        <div className="finding-content">
          <div
            className={`finding-paragraph-preview cc-client-request-content${
              isAiProcessing ? ' cc-client-request-content--ai-processing' : ''
            }${hideInsDelMarkup ? ' hide-ins-del' : ''}`}
            dir={paragraphDir}
          >
            {displayHtml ? (
              <div className="cc-paragraph-xml-display" dir={paragraphDir}>
                <div
                  className="cc-selected-text-xml"
                  dir={paragraphDir}
                  dangerouslySetInnerHTML={{ __html: displayHtml }}
                />
              </div>
            ) : (
              <p className="paragraph-text" dir={paragraphDir}>
                {plainParagraphText}
              </p>
            )}
          </div>

          {actionButtons}

          {ai?.status === 'error' && ai.error ? (
            <div className="finding-ai-error" role="alert">
              {ai.error}
            </div>
          ) : null}

          {isExplanationExpanded && (
            <div className="explanation-content">
              <div className="explanation-section">
                <strong>{t('review.ourSideInterest')}</strong>
                <p dir={detectTextDirection(finding.explanation.ourSideInterest)}>
                  {finding.explanation.ourSideInterest}
                </p>
              </div>
              <div className="explanation-section">
                <strong>{t('review.theirSideInterest')}</strong>
                <p dir={detectTextDirection(finding.explanation.theirSideInterest)}>
                  {finding.explanation.theirSideInterest}
                </p>
              </div>
              <div className="explanation-section">
                <strong>{t('review.whyThisMatters')}</strong>
                <p dir={detectTextDirection(whyText)}>{whyText}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCategory = (category: 'critical' | 'warning' | 'info', icon: string) => {
    const isExpanded = expandedCategories.has(category);
    const categoryFindings = findings?.[category] ?? [];
    const count = categoryFindings.length;
    const title = t(`review.categories.${category}`);

    return (
      <div className={`review-category ${category}`}>
        <div className="category-header" onClick={() => onToggleCategory(category)}>
          <span className="category-icon">{icon}</span>
          <span className="category-title">{title}</span>
          <span className="category-count">{count}</span>
          <span className={`category-caret ${isExpanded ? 'expanded' : ''}`}>{isExpanded ? '▼' : '▶'}</span>
        </div>

        {isExpanded && (
          <div className="category-content">
            {count === 0 ? (
              <div className="no-findings">{t('review.noFindings', { category: title })}</div>
            ) : (
              categoryFindings.map((finding, index) => renderFinding(finding, category, index))
            )}
          </div>
        )}
      </div>
    );
  };

  if (!findings) {
    return (
      <div className="review-results-container" dir={chromeDir}>
        <div className="no-findings">{t('review.noFindingsAvailable')}</div>
      </div>
    );
  }

  return (
    <div className="review-results-container" dir={chromeDir}>
      <div className="results-header">
        <h2>{t('review.documentReview')}</h2>
        <div className="header-actions">
          {onRestart && (
            <button
              type="button"
              className="restart-button"
              onClick={onRestart}
              title={t('review.restartTitle')}
              aria-label={t('review.restart')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
                <path d="M21 3v5h-5"></path>
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
                <path d="M3 21v-5h5"></path>
              </svg>
              <span>{t('review.restart')}</span>
            </button>
          )}
          <button
            type="button"
            className={`view-mode-button ${hideInsDelMarkup ? 'active' : ''}`}
            onClick={() => setHideInsDelMarkup((v) => !v)}
            title={
              hideInsDelMarkup
                ? t('review.showInsertionsAndDeletions')
                : t('review.hideInsertionsAndDeletions')
            }
            aria-label={
              hideInsDelMarkup
                ? t('review.showInsertionsAndDeletions')
                : t('review.hideInsertionsAndDeletions')
            }
            aria-pressed={hideInsDelMarkup}
          >
            <EyeOff size={16} />
            <span>{t('review.noMarkup')}</span>
          </button>
          <button
            type="button"
            className={`view-mode-button ${viewMode === 'chronological' ? 'active' : ''}`}
            onClick={toggleViewMode}
            title={
              viewMode === 'chronological'
                ? t('review.groupBySeverity')
                : t('review.showInDocumentOrder')
            }
            aria-label={
              viewMode === 'chronological'
                ? t('review.switchToSeverity')
                : t('review.switchToChronological')
            }
            aria-pressed={viewMode === 'chronological'}
          >
            <ListOrdered size={16} />
            <span>{t('review.chronological')}</span>
          </button>
        </div>
      </div>

      {viewMode === 'chronological' ? (
        <div className="categories-list chronological-list">
          {chronologicalFindings.length === 0 ? (
            <div className="no-findings">{t('review.noFindingsAvailable')}</div>
          ) : (
            chronologicalFindings.map((row, index) =>
              renderFinding(row.finding, row.category, index, { showSeverity: true }),
            )
          )}
        </div>
      ) : (
        <div className="categories-list">
          {renderCategory('critical', '●')}
          {renderCategory('warning', '▲')}
          {renderCategory('info', '●')}
        </div>
      )}
    </div>
  );
};
